import api, { API_BASE_URL } from './api';

export interface BoardImageMeta {
  image_id: string;
  name: string;
  url: string;
  width?: number;
  height?: number;
  mime_type?: string;
  size?: number;
}

export interface BoardStatePayload {
  images: Array<{
    id: string;
    name: string;
    url?: string; // dla nowych: po uploadzie
    dataUrl?: string; // lokalny fallback (tymczasowy)
    x: number;
    y: number;
    scale: number;
    width?: number;
    height?: number;
    drawingScale?: number;
    rotation?: number;
    measurements?: Array<{ start: { x: number; y: number }; end: { x: number; y: number }; valueMeters: number }>;
    areas?: Array<{ type: 'rect'|'poly'; points: Array<{x:number;y:number}>; valueM2: number; perimeterM?: number }>;
    annotations?: Array<unknown>;
    comments?: Array<{
      id: string; x: number; y: number; text: string;
      authorId?: string; authorDisplayName?: string; authorAvatarUrl?: string; createdAt?: string;
      mentions?: string[]; mentionNotified?: boolean;
      replies?: Array<{ id: string; text: string; authorId?: string; authorDisplayName?: string; authorAvatarUrl?: string; createdAt?: string }>;
    }>;
  }>;
  layers?: Array<{ id: string; name: string; color: string; visible: boolean }>;
  activeLayerId?: string;
  boardAnnotations?: Array<{ type: string; [k: string]: unknown }>;
}

const SERVER_BASE_URL = (API_BASE_URL || '').replace(/\/?api\/?$/i, '');
const normalizeUrl = (u?: string): string | undefined => {
  if (!u) return u;
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith('/')) return `${SERVER_BASE_URL}${u}`;
  return `${SERVER_BASE_URL}/${u.replace(/^\/+/, '')}`;
};
const denormalizeUrl = (u?: string): string | undefined => {
  if (!u) return u;
  // Jeśli front zapisuje absolutny URL do backendu, zamień go na względny /uploads/... do przechowywania w DB
  try {
    const base = SERVER_BASE_URL.replace(/\/$/, '');
    if (u.startsWith(base)) return u.slice(base.length) || '/';
    return u;
  } catch { return u; }
};

const BATCH_SIZE = 5; // plików na paczkę – mniejsze requesty, mniejsze ryzyko timeoutu

export async function uploadBoardImages(projectId: string, files: File[], opts?: { onUploadProgress?: (p: number) => void }): Promise<BoardImageMeta[]> {
  const allResults: BoardImageMeta[] = [];
  const batches: File[][] = [];
  for (let i = 0; i < files.length; i += BATCH_SIZE) {
    batches.push(files.slice(i, i + BATCH_SIZE));
  }
  const totalBatches = batches.length;

  for (let b = 0; b < batches.length; b++) {
    const batch = batches[b];
    const form = new FormData();
    batch.forEach(f => form.append('files', f));
    const res = await api.post<{ success: boolean; data: BoardImageMeta[] }>(`/board/${encodeURIComponent(projectId)}/images`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000, // 2 min na paczkę
      onUploadProgress: (e: any) => {
        try {
          if (opts?.onUploadProgress) {
            const total = e?.total || e?.currentTarget?.getResponseHeader?.('content-length') || 0;
            const loaded = e?.loaded || 0;
            const batchPct = total ? loaded / total : 1;
            const overall = ((b + batchPct) / totalBatches) * 100;
            opts.onUploadProgress(Math.round(overall));
          }
        } catch {}
      },
    });
    const items = (res.data.data || []).map(it => ({ ...it, url: normalizeUrl(it.url) as string }));
    allResults.push(...items);
  }

  return allResults;
}

export async function listBoardImages(projectId: string): Promise<BoardImageMeta[]> {
  const res = await api.get<{ success: boolean; data: BoardImageMeta[] }>(`/board/${encodeURIComponent(projectId)}/images`, { params: { _ts: Date.now() } });
  const items = res.data.data || [];
  return items.map(it => ({ ...it, url: normalizeUrl(it.url) as string }));
}

export async function deleteBoardImage(projectId: string, imageId: string): Promise<boolean> {
  const res = await api.delete<{ success: boolean }>(`/board/${encodeURIComponent(projectId)}/images/${encodeURIComponent(imageId)}`);
  return !!res.data.success;
}

export async function getBoardState(projectId: string): Promise<BoardStatePayload | null> {
  const res = await api.get<{ success: boolean; data: BoardStatePayload | null }>(`/board/${encodeURIComponent(projectId)}/state`, { params: { _ts: Date.now() }, headers: { 'Cache-Control': 'no-cache' } });
  const data = res.data.data || null;
  if (!data) return null;
  const images = (data.images || []).map(it => ({ ...it, url: normalizeUrl(it.url) }));
  return { ...data, images } as BoardStatePayload;
}

export async function putBoardState(projectId: string, state: BoardStatePayload): Promise<void> {
  // Zapisuj względne URL-e, żeby były przenośne między środowiskami
  const payload = {
    ...state,
    images: (state.images || []).map(it => ({ ...it, url: denormalizeUrl(it.url) }))
  };
  await api.put(`/board/${encodeURIComponent(projectId)}/state`, payload);
}

/** Zapis stanu przy zamknięciu strony – fetch z keepalive, żeby request się dokończył */
export function flushBoardStateOnUnload(projectId: string, state: BoardStatePayload): void {
  const payload = {
    ...state,
    images: (state.images || []).map(it => ({ ...it, url: denormalizeUrl(it.url) }))
  };
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const url = `${API_BASE_URL}/board/${encodeURIComponent(projectId)}/state`;
  fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {});
}

export async function addBoardImageByUrl(projectId: string, url: string, name?: string): Promise<BoardImageMeta> {
  const res = await api.post<{ success: boolean; data: BoardImageMeta }>(`/board/${encodeURIComponent(projectId)}/images/url`, { url, name });
  const item = res.data.data;
  return { ...item, url: normalizeUrl(item.url) as string };
}

export async function getPdfInfo(projectId: string, file: File): Promise<{ pages: number }>{
  const form = new FormData();
  form.append('file', file);
  const res = await api.post<{ success: boolean; data?: { pages: number } }>(`/board/${encodeURIComponent(projectId)}/pdf-info`, form, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  const pages = Number(res.data?.data?.pages || 0) || 0;
  return { pages };
}


